import { isBreakpointScreen, mediaQueries } from 'pricing-pages-ui-components/constants/Breakpoints';
import { useCallback, useSyncExternalStore } from 'react';

/**
 * @description This hook is used to check if the current window matches a given media query.
 * @param query - The media query to check; can be a breakpoint screen or a media query.
 *
 * @see `pricing-pages-ui-components/constants/Breakpoints.ts`
 *
 * @example
 * `useMedia('tabletPortraitLarge')`
 * `useMedia('${mediaQueries.phone} and ${mediaQueries.tablet}')`
 * `useMedia('(min-width: 768px) and (max-width: 1024px)')`
 *
 * Prefer `BreakpointScreen` arguments over custom media queries.
 *
 * NOTE: BreakPointScreen will use max-width media queries.
 *
 * @param initialState - The initial state of the media query.
 * @returns If the current window matches the media query.
 */
export function useMedia(query, initialState = false) {
  if (isBreakpointScreen(query)) {
    query = mediaQueries[query];
  }
  const subscribeMediaQuery = useCallback(onChange => {
    const mql = window.matchMedia(query);
    mql.addEventListener('change', onChange);
    return () => {
      mql.removeEventListener('change', onChange);
    };
  }, [query]);
  const matches = useSyncExternalStore(subscribeMediaQuery, () => window.matchMedia(query).matches, () => initialState);
  return matches;
}